import { useState, useEffect } from 'react'
import type { ComponentPropsWithoutRef, Ref, ReactNode } from 'react'
import { cn } from '#utils'
import { copyToClipboardSilent, isClipboardAvailable } from '#utils'
import { CopyIcon } from '#icons'
import { CheckmarkIcon } from '#icons'

/**
 * CopyButton component
 * 
 * A primitive button for copying text to clipboard with visual feedback.
 * Composable and flexible - consumer controls toast notifications and styling.
 * 
 * Features:
 * - Automatic clipboard availability detection (returns null if unavailable)
 * - Visual feedback with icon state change (copy → checkmark)
 * - Accessible with proper ARIA labels and live region announcements
 * - Size variants for different contexts
 * - Fully stylable with className overrides
 * - Exposes onCopySuccess and onCopyError callbacks for custom feedback
 * - Default brand purple color, overridable via className
 * 
 * @example
 * ```tsx
 * // Basic usage (default purple)
 * <CopyButton text="ABC123" />
 * ```
 * 
 * @example
 * ```tsx
 * // With custom success handling
 * <CopyButton 
 *   text="booking-id-123"
 *   onCopySuccess={() => butterToast.success('Booking ID copied!')}
 *   size="sm"
 * />
 * ```
 * 
 * @example
 * ```tsx
 * // As a composed element (inside an input trailing slot)
 * <Input 
 *   value={apiKey}
 *   trailing={<CopyButton text={apiKey} size="sm" />}
 * />
 * ```
 * 
 * @example
 * ```tsx
 * // Custom color styling (override default purple)
 * <CopyButton 
 *   text="custom-value"
 *   className="text-butter-gray-500 hover:text-butter-gray-900"
 *   iconClassName="h-5 w-5"
 * />
 * ```
 */
export interface CopyButtonProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children' | 'onClick'> {
  /**
   * Text to copy to clipboard
   */
  text: string
  
  /**
   * Button size variant
   * @default 'default'
   */
  size?: 'sm' | 'default' | 'lg'
  
  /**
   * Custom icon class name for sizing/styling
   * @default undefined (uses size-based defaults)
   */
  iconClassName?: string
  
  /**
   * Custom aria-label for the button
   * @default 'Copy to clipboard'
   */
  ariaLabel?: string
  
  /**
   * Duration in milliseconds to show the success state
   * @default 2000
   */
  successDuration?: number
  
  /**
   * Callback fired on successful copy
   * Use this to show custom toast notifications
   */
  onCopySuccess?: () => void
  
  /**
   * Callback fired on copy failure
   * Use this to show custom error notifications
   */
  onCopyError?: (error: unknown) => void
  
  /**
   * Custom icon to show in default state
   * @default <CopyIcon />
   */
  icon?: ReactNode
  
  /**
   * Custom icon to show in success state
   * @default <CheckmarkIcon />
   */
  successIcon?: ReactNode
}

export function CopyButton({
  text,
  size = 'default',
  iconClassName,
  ariaLabel = 'Copy to clipboard',
  successDuration = 2000,
  onCopySuccess,
  onCopyError,
  icon,
  successIcon,
  className,
  disabled,
  type = 'button',
  ref,
  ...props
}: CopyButtonProps & { ref?: Ref<HTMLButtonElement> }) {
  const [copied, setCopied] = useState(false)
  
  // Hide button if clipboard is not available
  const isAvailable = isClipboardAvailable()
  
  useEffect(() => {
    if (!copied) return
    
    const timeout = setTimeout(() => {
      setCopied(false)
    }, successDuration)
    
    return () => clearTimeout(timeout)
  }, [copied, successDuration])
  
  const handleCopy = async () => {
    const success = await copyToClipboardSilent(text)
    
    if (success) {
      setCopied(true)
      onCopySuccess?.()
    } else {
      onCopyError?.(new Error('Failed to copy to clipboard'))
    }
  }
  
  if (!isAvailable) {
    return null
  }
  
  // Size-based icon classes
  const defaultIconSizes = {
    sm: 'h-3 w-3',
    default: 'h-4 w-4',
    lg: 'h-5 w-5',
  }
  
  const iconSize = iconClassName || defaultIconSizes[size]
  
  // Size-based padding
  const sizeClasses = {
    sm: 'p-0.5',
    default: 'p-1',
    lg: 'p-1.5',
  }
  
  return (
    <>
      <button
        ref={ref}
        type={type}
        onClick={handleCopy}
        disabled={disabled}
        data-copied={copied}
        data-size={size}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center rounded',
          'text-butter-brand-600 transition-colors',
          // Hover state
          'hover:bg-butter-gray-100 hover:text-butter-brand-700',
          // Focus styles (WCAG 2.1 AA)
          'focus:outline-none focus:ring-2 focus:ring-butter-brand-600 focus:ring-offset-2',
          // Disabled state
          'disabled:pointer-events-none disabled:opacity-50',
          // Size padding
          sizeClasses[size],
          // User overrides
          className
        )}
        aria-label={copied ? 'Copied to clipboard' : ariaLabel}
        title={copied ? 'Copied!' : 'Copy to clipboard'}
        {...props}
      >
        {copied ? (
          successIcon || <CheckmarkIcon className={cn(iconSize, 'text-green-600')} />
        ) : (
          icon || <CopyIcon className={iconSize} />
        )}
      </button>
      
      {/* Screen reader announcement for copy success */}
      <span
        className="sr-only"
        aria-live="polite"
        role="status"
      >
        {copied ? 'Copied to clipboard' : ''}
      </span>
    </>
  )
}
